import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, EmptyState, Icon, IconButton, Input, Modal, Sheet, SkeletonText, useToast } from "@astrum/ui";
import { resolveMentions } from "@astrum/shared";
import { useSearchParams } from "react-router";
import { api, ApiError } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { applyFormat, buildDocTree, visibleDocNodes } from "../lib/doc-pages.js";
import type { DocPageSummary, FormatKind } from "../lib/doc-pages.js";
import { downloadPdf } from "../lib/export-pdf.js";
import { MessageBody } from "../routes/chat/MessageBody.js";
import { MentionAutocomplete } from "./MentionAutocomplete.js";
import type { InfoPanelMember } from "./InfoPanel.js";
import "./project-docs-tab.css";

/**
 * A project's docs: a page tree on the left, the open page on the right —
 * rendered, or in a split editor (markdown beside its preview).
 *
 * Pages are plain markdown rendered by the same safe renderer chat uses (never
 * HTML). Saves carry the page version; if somebody saved first the server
 * answers 409 with the latest page and the editor offers to load it or to
 * overwrite it, rather than silently losing either. The open page is in the
 * URL (`?tab=docs&doc=<id>`) so it can be linked.
 */

type DocPage = DocPageSummary & {
  projectId: string;
  body: string;
  lastEditedById: string | null;
};
type Revision = { id: string; version: number; title: string; editedByName: string; createdAt: string };
type RevisionFull = Revision & { body: string };

const TOOLBAR: { kind: FormatKind; label: string; icon?: string; text?: string }[] = [
  { kind: "h2", label: "Heading", text: "H" },
  { kind: "bold", label: "Bold", text: "B" },
  { kind: "italic", label: "Italic", text: "I" },
  { kind: "code", label: "Code", text: "</>" },
  { kind: "link", label: "Link", icon: "link" },
  { kind: "bullet", label: "Bulleted list", text: "•" },
  { kind: "number", label: "Numbered list", text: "1." },
  { kind: "task", label: "Task list", icon: "check" },
  { kind: "quote", label: "Quote", text: "”" },
];

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

export function ProjectDocsTab({
  projectId,
  members,
  canEdit,
}: {
  projectId: string;
  members: InfoPanelMember[];
  /** Whether the viewer may create and edit pages (the server enforces it). */
  canEdit: boolean;
}) {
  const { toast } = useToast();
  const auth = useAuth();
  const viewerId = auth.viewer?.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get("doc");

  const [pages, setPages] = useState<DocPageSummary[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [page, setPage] = useState<DocPage | null>(null);
  const [missing, setMissing] = useState(false);

  // Editing state: a draft over the loaded page, and the version it is based on.
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [baseVersion, setBaseVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState<DocPage | null>(null);
  const [caret, setCaret] = useState(0);
  const areaRef = useRef<HTMLTextAreaElement | null>(null);

  const [creating, setCreating] = useState<{ parentId: string | null } | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const select = useCallback(
    (id: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (id) next.set("doc", id);
      else next.delete("doc");
      next.set("tab", "docs");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const loadList = useCallback(async () => {
    const res = await api.get<{ pages: DocPageSummary[]; nextCursor: string | null }>(`/projects/${projectId}/docs?limit=500`);
    setPages(res.pages);
    setTruncated(res.nextCursor !== null);
  }, [projectId]);

  useEffect(() => {
    loadList().catch((err: unknown) => {
      setPages([]);
      toast(err instanceof ApiError ? err.message : "Could not load docs.", { tone: "danger" });
    });
  }, [loadList, toast]);

  // Load the open page; leaving edit mode when it changes.
  useEffect(() => {
    setEditing(false);
    setConflict(null);
    setMissing(false);
    if (!openId) {
      setPage(null);
      return;
    }
    let live = true;
    api
      .get<{ page: DocPage }>(`/docs/${openId}`)
      .then((res) => live && setPage(res.page))
      .catch(() => {
        if (live) {
          setPage(null);
          setMissing(true);
        }
      });
    return () => {
      live = false;
    };
  }, [openId]);

  const tree = useMemo(() => buildDocTree(pages ?? []), [pages]);
  const rows = useMemo(() => visibleDocNodes(tree, collapsed), [tree, collapsed]);
  const people = useMemo(
    () => members.map((m) => ({ id: m.id, displayName: m.displayName, jobTitle: m.jobTitle, avatarUrl: m.avatarUrl })),
    [members],
  );

  const startEdit = () => {
    if (!page) return;
    setTitle(page.title);
    // Wire-form mentions are shown as readable @Name while editing.
    setBody(page.body.replace(/@\[([^\]]{1,120})\]\([0-9a-fA-F-]{36}\)/g, "@$1"));
    setBaseVersion(page.version);
    setConflict(null);
    setEditing(true);
  };

  const dirty = editing && page !== null && (title !== page.title || resolve(body) !== page.body);

  function resolve(text: string) {
    return resolveMentions(text, people);
  }

  const save = async () => {
    if (!page || saving) return;
    setSaving(true);
    try {
      const res = await api.patch<{ page: DocPage }>(`/docs/${page.id}`, {
        version: baseVersion,
        title: title.trim() || page.title,
        body: resolve(body),
      });
      setPage(res.page);
      setEditing(false);
      setConflict(null);
      void loadList();
    } catch (err) {
      if (err instanceof ApiError && err.isConflict) {
        // The 409 body carries the latest page too, but ApiError keeps only the
        // message, so ask for it.
        const latest = await api.get<{ page: DocPage }>(`/docs/${page.id}`).then((r) => r.page).catch(() => null);
        if (latest) setConflict(latest);
        else toast("Someone else changed this page.", { tone: "danger" });
      } else {
        toast(err instanceof ApiError ? err.message : "Could not save.", { tone: "danger" });
      }
    } finally {
      setSaving(false);
    }
  };

  const loadLatest = () => {
    if (!conflict) return;
    setPage(conflict);
    setEditing(false);
    setConflict(null);
    void loadList();
  };

  /** Keep my text and save over the other version. */
  const overwrite = () => {
    if (!conflict) return;
    setBaseVersion(conflict.version);
    setPage(conflict);
    setConflict(null);
    toast("Your text is kept. Save again to replace their version.");
  };

  const create = async () => {
    const name = newTitle.trim();
    if (!name || !creating) return;
    try {
      const res = await api.post<{ page: DocPage }>(`/projects/${projectId}/docs`, { title: name, parentId: creating.parentId });
      setCreating(null);
      setNewTitle("");
      await loadList();
      select(res.page.id);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Could not create the page.", { tone: "danger" });
    }
  };

  const remove = async () => {
    if (!page) return;
    try {
      await api.delete(`/docs/${page.id}`);
      setDeleting(false);
      setPage(null);
      await loadList();
      select(null);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Could not delete the page.", { tone: "danger" });
    }
  };

  const format = (kind: FormatKind) => {
    const node = areaRef.current;
    if (!node) return;
    const edit = applyFormat(body, node.selectionStart, node.selectionEnd, kind);
    setBody(edit.value);
    requestAnimationFrame(() => {
      node.focus();
      node.setSelectionRange(edit.start, edit.end);
      setCaret(edit.end);
    });
  };

  const exportMd = () =>
    page &&
    downloadPdf(`/docs/${page.id}/export`, `${page.title.replace(/[^\p{L}\p{N}]+/gu, "-").slice(0, 60) || "page"}.md`).catch(() =>
      toast("Could not export the page.", { tone: "danger" }),
    );

  if (pages === null) return <SkeletonText lines={6} />;

  return (
    <div className="docs-tab">
      <nav className="docs-tree" aria-label="Pages">
        <div className="docs-tree__head">
          <span>Pages</span>
          {canEdit && (
            <IconButton
              icon={<Icon name="plus" size={14} />}
              label="New page"
              onClick={() => setCreating({ parentId: null })}
            />
          )}
        </div>
        {rows.length === 0 && <p className="docs-tree__empty">No pages yet.</p>}
        <ul>
          {rows.map(({ page: p, depth, children }) => (
            <li key={p.id} style={{ paddingLeft: depth * 14 }}>
              <div className={`docs-tree__row ${p.id === openId ? "is-active" : ""}`}>
                {children.length > 0 ? (
                  <button
                    type="button"
                    className="docs-tree__twirl"
                    aria-label={collapsed.has(p.id) ? "Expand" : "Collapse"}
                    aria-expanded={!collapsed.has(p.id)}
                    onClick={() =>
                      setCollapsed((prev) => {
                        const next = new Set(prev);
                        if (!next.delete(p.id)) next.add(p.id);
                        return next;
                      })
                    }
                  >
                    {collapsed.has(p.id) ? "▸" : "▾"}
                  </button>
                ) : (
                  <span className="docs-tree__twirl" aria-hidden="true" />
                )}
                <button type="button" className="docs-tree__title" onClick={() => select(p.id)} aria-current={p.id === openId ? "page" : undefined}>
                  {p.title}
                </button>
                {canEdit && (
                  <button
                    type="button"
                    className="docs-tree__add"
                    aria-label={`Add a page under ${p.title}`}
                    onClick={() => setCreating({ parentId: p.id })}
                  >
                    +
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
        {truncated && <p className="docs-tree__empty">Showing the first 500 pages.</p>}
      </nav>

      <section className="docs-main">
        {!openId && (
          <EmptyState
            title={rows.length ? "Pick a page" : "No docs yet"}
            description={
              rows.length
                ? "Choose a page from the list."
                : canEdit
                  ? "Write the project's notes, decisions and how-tos here."
                  : "Nothing has been written for this project yet."
            }
            action={canEdit && !rows.length ? <Button onClick={() => setCreating({ parentId: null })}>New page</Button> : undefined}
          />
        )}
        {openId && missing && <EmptyState title="Page not found" description="It may have been deleted." />}
        {openId && !missing && !page && <SkeletonText lines={5} />}

        {page && (
          <>
            <header className="docs-main__head">
              {editing ? (
                <Input aria-label="Page title" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
              ) : (
                <h2 className="docs-main__title">{page.title}</h2>
              )}
              <div className="docs-main__actions">
                {editing ? (
                  <>
                    <Button variant="ghost" onClick={() => setEditing(false)}>
                      Cancel
                    </Button>
                    <Button onClick={save} disabled={saving || !dirty}>
                      {saving ? "Saving…" : "Save"}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="ghost" onClick={() => setHistoryOpen(true)}>
                      History
                    </Button>
                    <Button variant="ghost" onClick={exportMd}>
                      Export
                    </Button>
                    {canEdit && (
                      <>
                        <Button variant="ghost" onClick={() => setDeleting(true)}>
                          Delete
                        </Button>
                        <Button onClick={startEdit}>Edit</Button>
                      </>
                    )}
                  </>
                )}
              </div>
            </header>
            <p className="docs-main__meta">
              {page.lastEditedByName ? `Last edited by ${page.lastEditedByName}, ` : "Last edited "}
              {when(page.updatedAt)}
            </p>

            {conflict && (
              <div className="docs-conflict" role="alert">
                <strong>{conflict.lastEditedByName ?? "Someone"} saved a newer version of this page.</strong>
                <span> Your edits have not been lost yet.</span>
                <div className="docs-conflict__actions">
                  <Button variant="ghost" onClick={loadLatest}>
                    Discard mine, load theirs
                  </Button>
                  <Button onClick={overwrite}>Keep mine</Button>
                </div>
              </div>
            )}

            {editing ? (
              <div className="docs-editor">
                <div className="docs-editor__toolbar" role="toolbar" aria-label="Formatting">
                  {TOOLBAR.map((t) => (
                    <button key={t.kind} type="button" title={t.label} aria-label={t.label} onClick={() => format(t.kind)}>
                      {t.icon ? <Icon name={t.icon as "link"} size={14} /> : t.text}
                    </button>
                  ))}
                </div>
                <div className="docs-editor__split">
                  <div className="docs-editor__pane">
                    <textarea
                      ref={areaRef}
                      className="docs-editor__area"
                      aria-label="Page body (markdown)"
                      value={body}
                      maxLength={200_000}
                      onChange={(e) => {
                        setBody(e.target.value);
                        setCaret(e.target.selectionStart);
                      }}
                      onSelect={(e) => setCaret(e.currentTarget.selectionStart)}
                    />
                    <MentionAutocomplete
                      value={body}
                      caret={caret}
                      people={people}
                      onPick={(next, nextCaret) => {
                        setBody(next);
                        setCaret(nextCaret);
                        requestAnimationFrame(() => {
                          areaRef.current?.focus();
                          areaRef.current?.setSelectionRange(nextCaret, nextCaret);
                        });
                      }}
                    />
                  </div>
                  <div className="docs-editor__preview" aria-label="Preview">
                    <MessageBody body={resolve(body)} viewerId={viewerId} rich />
                  </div>
                </div>
              </div>
            ) : (
              <div className="docs-read">
                {page.body.trim() ? (
                  <MessageBody body={page.body} viewerId={viewerId} rich />
                ) : (
                  <p className="docs-tree__empty">This page is empty.</p>
                )}
              </div>
            )}
          </>
        )}
      </section>

      <Modal open={creating !== null} onClose={() => setCreating(null)} title="New page">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
          className="docs-new"
        >
          <Input label="Title" value={newTitle} maxLength={200} onChange={(e) => setNewTitle(e.target.value)} autoFocus />
          <div className="docs-conflict__actions">
            <Button type="button" variant="ghost" onClick={() => setCreating(null)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!newTitle.trim()}>
              Create
            </Button>
          </div>
        </form>
      </Modal>

      <Modal open={deleting} onClose={() => setDeleting(false)} title="Delete this page?">
        <p>Pages nested under it move up a level. Its history is kept.</p>
        <div className="docs-conflict__actions">
          <Button variant="ghost" onClick={() => setDeleting(false)}>
            Cancel
          </Button>
          <Button onClick={remove}>Delete</Button>
        </div>
      </Modal>

      {page && (
        <HistorySheet
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          page={page}
          canEdit={canEdit}
          viewerId={viewerId}
          onRestored={(restored) => {
            setPage(restored);
            setHistoryOpen(false);
            void loadList();
            toast("Version restored.");
          }}
        />
      )}
    </div>
  );
}

/* ───────────────────────────── history drawer ───────────────────────────── */

function HistorySheet({
  open,
  onClose,
  page,
  canEdit,
  viewerId,
  onRestored,
}: {
  open: boolean;
  onClose: () => void;
  page: DocPage;
  canEdit: boolean;
  viewerId: string | undefined;
  onRestored: (page: DocPage) => void;
}) {
  const { toast } = useToast();
  const [revisions, setRevisions] = useState<Revision[] | null>(null);
  const [more, setMore] = useState<number | null>(null);
  const [shown, setShown] = useState<RevisionFull | null>(null);

  useEffect(() => {
    if (!open) return;
    setRevisions(null);
    setShown(null);
    api
      .get<{ revisions: Revision[]; nextCursor: number | null }>(`/docs/${page.id}/revisions`)
      .then((res) => {
        setRevisions(res.revisions);
        setMore(res.nextCursor);
      })
      .catch(() => setRevisions([]));
  }, [open, page.id, page.version]);

  const loadMore = async () => {
    if (more === null) return;
    const res = await api.get<{ revisions: Revision[]; nextCursor: number | null }>(`/docs/${page.id}/revisions?before=${more}`);
    setRevisions((prev) => [...(prev ?? []), ...res.revisions]);
    setMore(res.nextCursor);
  };

  const view = async (id: string) => {
    const res = await api.get<{ revision: RevisionFull }>(`/docs/${page.id}/revisions/${id}`);
    setShown(res.revision);
  };

  const restore = async () => {
    if (!shown) return;
    try {
      const res = await api.post<{ page: DocPage }>(`/docs/${page.id}/revisions/${shown.id}/restore`, { version: page.version });
      onRestored(res.page);
    } catch (err) {
      toast(err instanceof ApiError && err.isConflict ? "The page changed meanwhile. Reload and try again." : "Could not restore.", {
        tone: "danger",
      });
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Page history">
      <div className="docs-history">
        {revisions === null && <SkeletonText lines={4} />}
        <ul className="docs-history__list">
          {revisions?.map((r) => (
            <li key={r.id}>
              <button type="button" className={shown?.id === r.id ? "is-active" : ""} onClick={() => void view(r.id)}>
                <strong>{when(r.createdAt)}</strong>
                <span>{r.editedByName}</span>
              </button>
            </li>
          ))}
        </ul>
        {more !== null && (
          <Button variant="ghost" onClick={() => void loadMore()}>
            Older versions
          </Button>
        )}
        {shown && (
          <div className="docs-history__view">
            <h3>{shown.title}</h3>
            <MessageBody body={shown.body} viewerId={viewerId} rich />
            {canEdit && <Button onClick={() => void restore()}>Restore this version</Button>}
          </div>
        )}
      </div>
    </Sheet>
  );
}
